import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Глобальный error boundary вокруг дерева роутов. Без него любой render-throw
 * (например, малформед-карточка из LLM/бэка) ронял весь SPA в белый экран без
 * шанса восстановиться. Здесь ловим, логируем и показываем экран с кнопкой
 * перезагрузки. Стили — инлайновые: на момент краша Tailwind-классы/тема могли
 * не примениться, а экран всё равно должен быть читаемым.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Сюда позже можно подключить Sentry/логгер.
    console.error('App error boundary caught:', error, info);
  }

  private handleReload = (): void => {
    this.setState({ hasError: false });
    window.location.assign('/');
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          textAlign: 'center',
          color: 'var(--fg, #e7e7ea)',
          background: 'var(--bg, #0e0e10)',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 600 }}>Что-то пошло не так</div>
        <div style={{ opacity: 0.7, maxWidth: 360, lineHeight: 1.5 }}>
          Произошла непредвиденная ошибка. Попробуй перезагрузить страницу.
        </div>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '10px 20px',
            borderRadius: 12,
            border: 'none',
            cursor: 'pointer',
            background: 'var(--accent, #ff7a1a)',
            color: '#111',
            fontWeight: 600,
          }}
        >
          Перезагрузить
        </button>
      </div>
    );
  }
}
